import React from "react";
import Box from "@mui/material/Box";
import { Card, Skeleton, Typography } from "@mui/material";
import { PieChart } from "@mui/x-charts/PieChart";
import { SparkLineChart } from "@mui/x-charts/SparkLineChart";
import { dashboardWidgetCardSx, formatGbp } from "./dashboardDisplay";

export interface YtdPieLegendItem {
    id: number;
    label: string;
    value: number;
    units: number;
}

interface KpiStatCardProps {
    label: string;
    loading: boolean;
    value: string;
    caption?: string;
    captionColor?: string;
    sparkline?: number[];
    sparklineColor: string;
}

interface YtdPieWidgetProps {
    title: string;
    loading: boolean;
    items: YtdPieLegendItem[];
    emptyMessage: string;
    colors: string[];
    formatCurrency: (value: number) => string;
    formatUnits: (value: number) => string;
}

export const KpiStatCard: React.FC<KpiStatCardProps> = ({
    label,
    loading,
    value,
    caption,
    captionColor,
    sparkline,
    sparklineColor,
}) => (
    <Card variant="outlined" sx={dashboardWidgetCardSx}>
        <Typography
            variant="overline"
            color="text.secondary"
            sx={{ display: "block", letterSpacing: 0.8, fontWeight: 600, lineHeight: 1.3 }}
        >
            {label}
        </Typography>
        {loading ? (
            <>
                <Skeleton variant="text" width="70%" height={40} />
                <Skeleton variant="text" width="50%" height={20} />
                <Skeleton variant="rectangular" height={48} sx={{ mt: 1.5, borderRadius: 1 }} />
            </>
        ) : (
            <>
                <Typography
                    variant="h5"
                    sx={{ fontWeight: 600, letterSpacing: "-0.02em", mt: 0.5, lineHeight: 1.2 }}
                >
                    {value}
                </Typography>
                {caption ? (
                    <Typography variant="body2" sx={{ color: captionColor ?? "text.secondary", mt: 0.5 }}>
                        {caption}
                    </Typography>
                ) : null}
                {sparkline && sparkline.length > 1 ? (
                    <Box sx={{ mt: 1.5, height: 48, width: "100%", minWidth: 0 }}>
                        <SparkLineChart
                            data={sparkline}
                            height={48}
                            area
                            showTooltip
                            showHighlight
                            colors={[sparklineColor]}
                            valueFormatter={(value) => formatGbp(Number(value ?? 0))}
                            margin={{ top: 8, bottom: 2, left: 0, right: 0 }}
                        />
                    </Box>
                ) : (
                    <Box sx={{ mt: 1.5, height: 48 }} />
                )}
            </>
        )}
    </Card>
);

export const YtdPieWidget: React.FC<YtdPieWidgetProps> = ({
    title,
    loading,
    items,
    emptyMessage,
    colors,
    formatCurrency,
    formatUnits,
}) => (
    <Card
        variant="outlined"
        sx={{ ...dashboardWidgetCardSx, containerType: "inline-size" }}
    >
        <Typography variant="subtitle1" sx={{ fontWeight: 600, letterSpacing: "-0.01em" }}>
            {title}
        </Typography>
        {loading ? (
            <Skeleton variant="rectangular" width="100%" height={280} sx={{ mt: 2, borderRadius: 1 }} />
        ) : items.length > 0 ? (
            <Box
                sx={{
                    display: "flex",
                    flexDirection: { "@": "column", "@520": "row" },
                    alignItems: "center",
                    gap: 2.5,
                    mt: 2,
                    minHeight: 260,
                }}
            >
                <Box sx={{ flex: "0 0 auto", display: "flex", justifyContent: "center" }}>
                    <PieChart
                        width={200}
                        height={200}
                        series={[
                            {
                                data: items.map((item, index) => ({
                                    id: item.id,
                                    value: item.value,
                                    label: item.label,
                                    color: colors[index % colors.length],
                                })),
                                highlightScope: { faded: "global", highlighted: "item" },
                                faded: { innerRadius: 62, additionalRadius: -16, color: "gray" },
                                paddingAngle: 1.5,
                                cornerRadius: 4,
                                cx: 100,
                                cy: 100,
                                innerRadius: 62,
                                outerRadius: 88,
                            },
                        ]}
                        margin={{ left: 0, right: 0, top: 0, bottom: 0 }}
                        slotProps={{
                            legend: { hidden: true },
                        }}
                    />
                </Box>
                <Box sx={{ flex: "1 1 200px", minWidth: 0, width: { "@": "100%", "@520": "auto" } }}>
                    <Box component="ul" sx={{ listStyle: "none", p: 0, m: 0 }}>
                        {items.map((item, index) => (
                            <Box
                                component="li"
                                key={item.id}
                                sx={{
                                    display: "flex",
                                    alignItems: "flex-start",
                                    gap: 1,
                                    mb: 1,
                                    fontSize: "0.875rem",
                                }}
                            >
                                <Box
                                    sx={{
                                        width: 10,
                                        height: 10,
                                        borderRadius: "50%",
                                        backgroundColor: colors[index % colors.length],
                                        flexShrink: 0,
                                        mt: "5px",
                                    }}
                                />
                                <Typography
                                    variant="body2"
                                    sx={{
                                        flex: "1 1 auto",
                                        minWidth: 0,
                                        overflowWrap: "anywhere",
                                        pr: 1,
                                    }}
                                >
                                    {item.label}
                                </Typography>
                                <Box sx={{ textAlign: "right", flexShrink: 0, whiteSpace: "nowrap" }}>
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                        {formatCurrency(item.value)}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {formatUnits(item.units)} units
                                    </Typography>
                                </Box>
                            </Box>
                        ))}
                    </Box>
                </Box>
            </Box>
        ) : (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                {emptyMessage}
            </Typography>
        )}
    </Card>
);
